<template lang="pug">
div
  //- Header
  v-row(no-gutters align="center")
    v-col
      .yz-heading-8.text-neutral-700 Suppliers
      .yz-regular-body-sm.text-neutral-500.mt-1 Manage your suppliers and their contact persons.

  //- Search and Add new
  v-row.mt-4(no-gutters align="center")
    v-col
    v-col.shrink
      .d-flex.align-center.justify-end
        yz-search-btn(v-model="searchText" @search="onSearch")
        yz-btn.ml-3(label="Add new" size="lg" @click="onAddNew")

  yz-divider.my-2

  //- Data table
  yz-data-table.mt-2(:headers="headers" :items="suppliers" no-outer-border radius="0" body-height="52px" header-height="36px" header-background-color="white" hide-default-footer :loading="isFetching" @click:row="onEditItem")
    template(v-slot:item.name="{ item }")
      .yz-regular-body-sm.text-neutral-700 {{ item.value }}
    template(v-slot:item.pic="{ item }")
      .yz-regular-body-sm.text-neutral-700 {{ getDefaultContact(item).name || '-' }}
    template(v-slot:item.phone="{ item }")
      .yz-regular-body-sm.text-neutral-700 {{ getDefaultContact(item).phone || '-' }}
    template(v-slot:item.status="{ item }")
      div(@click.stop)
        yz-switch(v-model="item.enabled" size="md" @change="onUpdateStatus(item, item.enabled)")
    template(v-slot:item.actions="{ item, index }")
      .d-flex.justify-center(@click.stop)
        yz-menu(offset-y :top="menuPositionTop(index)" left)
          template(#activator="{ on }")
            yz-btn(icon="mdi-dots-horizontal" size="sm" icon-size="24" background-color="transparent" icon-color="neutral-500" v-on="on")
          yz-list
            yz-list-item(@click="onEditItem(item)") Edit
            yz-divider
            yz-list-item(@click="onDeleteItem(item)")
              span.danger500--text Delete

  //- Pagination
  yz-pagination.mt-4(v-if="totalCount > pageSize" v-model="currentPage" version="yc" :page-size.sync="pageSize" :count="totalCount" @input="onPageChanged")

  //- Dialogs
  add-new-supplier-dialog(v-if="openSupplierDialog" :open.sync="openSupplierDialog" :edit-supplier="selectedSupplier" @created="onSupplierSaved" @updated="onSupplierSaved" @closed="closed")
  delete-confirmation-dialog(v-if="openDeleteDialog" :open.sync="openDeleteDialog" :loading="isLoading" @cancel="closed" @delete="onConfirmDelete")
    .yz-heading-8 Are you sure you want to delete this supplier?
    .yz-regular-body-sm.mt-4 This action will be done immediately and can't be undone. Click "Confirm" to proceed
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AddNewSupplierDialog from '@/components/hq/inventory/branches-warehouses/dialog/AddNewSupplierDialog.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'

export default {
  components: { AddNewSupplierDialog, DeleteConfirmationDialog },
  data: () => ({
    isFetching: false,
    isLoading: false,
    pageSize: 20,
    currentPage: 1,
    searchText: '',
    totalCount: 0,
    suppliers: [],
    selectedSupplier: null,
    openSupplierDialog: false,
    openDeleteDialog: false,
    headers: [
      { text: 'NAME', value: 'name', sortable: false },
      { text: 'PIC', value: 'pic', sortable: false },
      { text: 'PHONE', value: 'phone', sortable: false },
      { text: 'STATUS', value: 'status', width: '100', sortable: false },
      { text: 'ACTION', value: 'actions', width: '80', sortable: false, align: 'end' }
    ]
  }),
  computed: {
    ...mapGetters({ company: 'getCompany' })
  },
  watch: {
    pageSize () {
      this.currentPage = 1
      this.fetchSuppliers()
    }
  },
  mounted () {
    this.fetchSuppliers()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchSuppliers () {
      this.isFetching = true
      const params = {
        page_size: this.pageSize,
        page: this.currentPage,
        company: this.company.id,
        field: 'medication.sold_by'
      }
      if (this.searchText) { params.value__contains = this.searchText }
      this.$axios.get('/options/', { params })
        .then(resp => {
          this.suppliers = resp.data.results
          this.totalCount = resp.data.count
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    onSearch () {
      this.currentPage = 1
      this.fetchSuppliers()
    },
    onUpdateStatus (item, status) {
      this.isFetching = true
      this.$axios.patch(`/options/${item.id}`, { enabled: status })
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          this.isFetching = false
        })
    },
    onConfirmDelete () {
      this.isLoading = true
      this.$axios.delete(`/options/${this.selectedSupplier.id}`)
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    onAddNew () {
      this.selectedSupplier = null
      this.openSupplierDialog = true
    },
    onEditItem (item) {
      this.selectedSupplier = item
      this.openSupplierDialog = true
    },
    onDeleteItem (item) {
      this.selectedSupplier = item
      this.openDeleteDialog = true
    },
    onSupplierSaved () {
      this.reset()
    },
    onPageChanged () {
      this.fetchSuppliers()
    },
    getDefaultContact (item) {
      const raw = item.extra_data
      const data = !raw ? [] : Array.isArray(raw) ? raw : [raw]
      return data.find(c => c.default) || data[0] || {}
    },
    reset () {
      this.currentPage = 1
      this.fetchSuppliers()
      this.selectedSupplier = null
      this.openSupplierDialog = false
      this.openDeleteDialog = false
    },
    closed () {
      this.selectedSupplier = null
      this.openSupplierDialog = false
      this.openDeleteDialog = false
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 6) {
        let start = this.suppliers.length - 2
        if (position > start) {
          return true
        }
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  cursor: pointer;
  background: var(--yz-neutral-200) !important;
}
</style>
